import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";

/** Chevron: down when closed, flips up when open. */
export function DropdownChevron({ open }) {
  return (
    <svg
      className={`so-dd__chevSvg ${open ? "so-dd__chevSvg--open" : ""}`}
      width="14"
      height="14"
      viewBox="0 0 12 12"
      aria-hidden="true"
    >
      <path
        d="M2.5 4.25L6 7.75L9.5 4.25"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.35"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Searchable dropdown — same portal/inline logic as CustomDropdown but renders
 * a search input at the top of the menu. Options with value "all" are always
 * shown regardless of the query.
 */
export function SearchableDropdown({ id, label, value, options, onChange, menuKey, openKey, onOpenToggle }) {
  const open = openKey === menuKey;
  const selected = options.find((o) => o.value === value) || options[0];
  const triggerRef = useRef(null);
  const searchRef  = useRef(null);
  const [menuPos,  setMenuPos]  = useState(null);
  const [search,   setSearch]   = useState("");

  const portalRoot = useMemo(() => (typeof document === "undefined" ? null : document.body), []);

  const inModal = useMemo(() => Boolean(triggerRef.current?.closest?.(".modal")), [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear search when dropdown closes
  useEffect(() => { if (!open) setSearch(""); }, [open]);

  // Auto-focus search input when menu opens
  useEffect(() => {
    if (open) setTimeout(() => searchRef.current?.focus(), 30);
  }, [open]);

  useEffect(() => {
    if (!open || inModal) { setMenuPos(null); return; }
    let raf = 0;
    const recompute = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const r = triggerRef.current?.getBoundingClientRect();
        if (!r) return;
        const gap = 8;
        const minW = Math.max(200, r.width);
        const vw = window.innerWidth || 0;
        const vh = window.innerHeight || 0;
        const maxLeft = Math.max(gap, vw - minW - gap);
        const left = Math.min(Math.max(gap, r.left), maxLeft);
        const maxMenuH = Math.max(220, Math.min(360, Math.floor(vh - gap * 2)));
        const belowTop = r.bottom + 6;
        const aboveTop = Math.max(gap, r.top - 6 - maxMenuH);
        const placeBelow = belowTop + 180 <= vh - gap;
        setMenuPos({ top: placeBelow ? belowTop : aboveTop, left, minWidth: minW, maxHeight: maxMenuH });
      });
    };
    recompute();
    window.addEventListener("resize", recompute);
    window.addEventListener("scroll", recompute, true);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", recompute); window.removeEventListener("scroll", recompute, true); };
  }, [open, inModal]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.value === "all" || o.label.toLowerCase().includes(q));
  }, [options, search]);

  const selectItem = useCallback((val) => { onChange(val); onOpenToggle(null); }, [onChange, onOpenToggle]);

  const renderList = (inline) => (
    <div className={`so-dd__portal${inline ? " so-dd__menu--inline" : ""}`} data-so-dropdown
      style={!inline && menuPos ? { position: "fixed", top: `${menuPos.top}px`, left: `${menuPos.left}px`, minWidth: `${menuPos.minWidth}px`, maxHeight: `${menuPos.maxHeight}px` } : undefined}
    >
      <div className="so-dd__search" onMouseDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()}>
        <input
          ref={searchRef}
          type="text"
          className="so-dd__searchInput"
          placeholder="Search…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onClick={(e) => e.stopPropagation()}
        />
      </div>
      <ul className="so-dd__menu so-dd__menu--tiles" role="listbox" style={{ maxHeight: inline ? 220 : undefined, overflowY: "auto" }}>
        {filtered.length === 0
          ? <li className="so-dd__menuItem so-dd__noResults">No results</li>
          : filtered.map((o) => (
            <li key={o.value} className="so-dd__menuItem" role="none">
              <button
                type="button"
                role="option"
                aria-selected={o.value === value}
                className={`so-dd__option so-dd__option--tile ${o.value === value ? "is-selected" : ""}`}
                onTouchStart={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => { e.stopPropagation(); selectItem(o.value); }}
              >
                {o.label}
              </button>
            </li>
          ))}
      </ul>
    </div>
  );

  return (
    <div className={`so-dd ${open ? "is-open" : ""}`} data-so-dropdown>
      <label className="so-dd__label" htmlFor={id} id={`${id}-lbl`}>{label}</label>
      <button
        type="button"
        id={id}
        ref={triggerRef}
        className={`so-dd__trigger ${open ? "is-open" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby={`${id}-lbl`}
        onTouchStart={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); onOpenToggle(open ? null : menuKey); }}
      >
        <span className="so-dd__value">{selected.label}</span>
        <DropdownChevron open={open} />
      </button>
      {open && inModal
        ? renderList(true)
        : open && portalRoot && menuPos
          ? createPortal(renderList(false), portalRoot)
          : null}
    </div>
  );
}

/** Shared custom dropdown (tiles menu) for Spot / Open Orders and similar pages. */
export function CustomDropdown({ id, label, value, options, onChange, menuKey, openKey, onOpenToggle }) {
  const open = openKey === menuKey;
  const selected = options.find((o) => o.value === value) || options[0];
  const triggerRef = useRef(null);
  const [menuPos, setMenuPos] = useState(null);

  const portalRoot = useMemo(() => {
    if (typeof document === "undefined") return null;
    return document.body;
  }, []);

  const inModal = useMemo(() => {
    const btn = triggerRef.current;
    return Boolean(btn?.closest?.(".modal"));
  }, [open]);

  useEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }
    const btn = triggerRef.current;
    if (!btn) return;

    // Mobile filters: when inside a Bootstrap modal, keep dropdown inline under
    // the trigger and scroll modal content so options are visible.
    if (inModal) {
      const modalContent = btn.closest?.(".modal-content");
      if (modalContent) {
        requestAnimationFrame(() => {
          const r = btn.getBoundingClientRect();
          const cr = modalContent.getBoundingClientRect();
          // Leave some room (approx menu height) below the trigger.
          const desiredBottom = cr.bottom - 220;
          if (r.bottom > desiredBottom) {
            modalContent.scrollTop += r.bottom - desiredBottom;
          }
        });
      }
      return;
    }

    let raf = 0;
    const recompute = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const r = btn.getBoundingClientRect();
        const gap = 8;
        const minW = Math.max(180, r.width);
        const vw = window.innerWidth || 0;
        const vh = window.innerHeight || 0;

        // Clamp horizontally so menu never renders off-screen.
        const maxLeft = Math.max(gap, vw - minW - gap);
        const left = Math.min(Math.max(gap, r.left), maxLeft);

        // Prefer below; if not enough space, render above.
        const maxMenuH = Math.max(180, Math.min(360, Math.floor(vh - gap * 2)));
        const belowTop = r.bottom + 6;
        const aboveTop = Math.max(gap, r.top - 6 - maxMenuH);
        const placeBelow = belowTop + 180 <= vh - gap;
        const top = placeBelow ? belowTop : aboveTop;

        setMenuPos({
          top,
          left,
          minWidth: minW,
          maxHeight: maxMenuH,
        });
      });
    };

    recompute();
    window.addEventListener("resize", recompute);
    window.addEventListener("scroll", recompute, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", recompute);
      window.removeEventListener("scroll", recompute, true);
    };
  }, [open, inModal]);

  return (
    <div className={`so-dd ${open ? "is-open" : ""}`} data-so-dropdown>
      <label className="so-dd__label" htmlFor={id} id={`${id}-lbl`}>
        {label}
      </label>
      <button
        type="button"
        id={id}
        ref={triggerRef}
        className={`so-dd__trigger ${open ? "is-open" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby={`${id}-lbl`}
        onTouchStart={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          onOpenToggle(open ? null : menuKey);
        }}
      >
        <span className="so-dd__value">{selected.label}</span>
        <DropdownChevron open={open} />
      </button>
      {open && inModal ? (
        <ul className="so-dd__menu so-dd__menu--tiles so-dd__menu--inline" role="listbox">
          {options.map((o) => (
            <li key={o.value} className="so-dd__menuItem" role="none">
              <button
                type="button"
                role="option"
                aria-selected={o.value === value}
                className={`so-dd__option so-dd__option--tile ${o.value === value ? "is-selected" : ""}`}
                onTouchStart={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(o.value);
                  onOpenToggle(null);
                }}
              >
                {o.label}
              </button>
            </li>
          ))}
        </ul>
      ) : open && portalRoot && menuPos ? (
        createPortal(
          <div
            className="so-dd__portal"
            style={{
              position: "fixed",
              top: `${menuPos.top}px`,
              left: `${menuPos.left}px`,
              minWidth: `${menuPos.minWidth}px`,
              maxHeight: `${menuPos.maxHeight}px`,
            }}
            data-so-dropdown
          >
            <ul className="so-dd__menu so-dd__menu--tiles" role="listbox">
              {options.map((o) => (
                <li key={o.value} className="so-dd__menuItem" role="none">
                  <button
                    type="button"
                    role="option"
                    aria-selected={o.value === value}
                    className={`so-dd__option so-dd__option--tile ${o.value === value ? "is-selected" : ""}`}
                    onTouchStart={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      onChange(o.value);
                      onOpenToggle(null);
                    }}
                  >
                    {o.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>,
          portalRoot
        )
      ) : null}
    </div>
  );
}
